import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  TrendingUp,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  Zap,
  BookmarkPlus,
  Bookmark,
  Trash2,
  ClipboardList,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { MOCK_TEAM, MOCK_PROJECTS, type TeamMember } from "./mockData";

// ── Types ─────────────────────────────────────────────────────────────────────

type Horizon = 3 | 6 | 12;

interface Scenario {
  id: string;
  name: string;
  newHires: number;
  attrition: number;
  horizon: Horizon;
}

type RiskLevel = "under" | "healthy" | "risk" | "critical";

// ── Helpers ───────────────────────────────────────────────────────────────────

function avgCapacity(members: TeamMember[]): number {
  if (!members.length) return 0;
  return Math.round(
    members.reduce((s, m) => s + m.capacityPercent, 0) / members.length,
  );
}

function riskLevel(cap: number): RiskLevel {
  if (cap > 100) return "critical";
  if (cap > 85) return "risk";
  if (cap >= 70) return "healthy";
  return "under";
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}

function SliderRow({ label, value, min, max, onChange }: SliderRowProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs font-semibold tabular-nums w-6 text-right">
          {value}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-muted [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
      />
    </div>
  );
}

interface MetricChipProps {
  value: string | number;
  label: string;
  delta?: number;
  positive?: boolean;
}

function MetricChip({ value, label, delta, positive }: MetricChipProps) {
  const showDelta = delta !== undefined && delta !== 0;
  return (
    <div className="flex-1 rounded-lg bg-card border border-border/60 px-3 py-2 text-center">
      <div className="flex items-center justify-center gap-1">
        <span className="text-base font-bold tabular-nums">{value}</span>
        {showDelta && (
          <span
            className={cn(
              "text-[10px] font-semibold",
              positive ? "text-emerald-400" : "text-rose-400",
            )}
          >
            {delta! > 0 ? `+${delta}` : delta}
          </span>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground mt-0.5 leading-none">
        {label}
      </p>
    </div>
  );
}

// ── Capacity Risk Badge ───────────────────────────────────────────────────────

function RiskBadge({ cap, t }: { cap: number; t: (k: string) => string }) {
  const lvl = riskLevel(cap);
  if (lvl === "critical")
    return (
      <Badge
        variant="outline"
        className="gap-1 border-rose-500/40 text-rose-400 bg-rose-500/10 text-xs"
      >
        <AlertTriangle className="h-3 w-3" />
        {t("teamOverview.riskCritical")}
      </Badge>
    );
  if (lvl === "risk")
    return (
      <Badge
        variant="outline"
        className="gap-1 border-amber-500/40 text-amber-400 bg-amber-500/10 text-xs"
      >
        <Zap className="h-3 w-3" />
        {t("teamOverview.riskOverload")}
      </Badge>
    );
  if (lvl === "healthy")
    return (
      <Badge
        variant="outline"
        className="gap-1 border-emerald-500/40 text-emerald-400 bg-emerald-500/10 text-xs"
      >
        <CheckCircle2 className="h-3 w-3" />
        {t("teamOverview.riskHealthy")}
      </Badge>
    );
  return (
    <Badge
      variant="outline"
      className="text-xs gap-1 border-sky-500/40 text-sky-400 bg-sky-500/10"
    >
      <TrendingUp className="h-3 w-3" />
      {t("teamOverview.riskUnder")}
    </Badge>
  );
}

// ── Hiring Plan Dialog ────────────────────────────────────────────────────────

function HiringPlanDialog({
  open,
  onClose,
  newHires,
  horizon,
  projectedCap,
  t,
}: {
  open: boolean;
  onClose: () => void;
  newHires: number;
  horizon: Horizon;
  projectedCap: number;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const roles = [
    "Senior Engineer",
    "Frontend Engineer",
    "Backend Engineer",
    "QA Engineer",
  ].slice(0, Math.max(1, newHires));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-indigo-500/10">
              <ClipboardList className="h-4 w-4 text-indigo-400" />
            </div>
            <DialogTitle className="text-sm font-semibold">
              {t("teamOverview.hiringPlanTitle")}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-border/60 bg-muted/30 p-3 text-center">
              <p className="text-xl font-bold text-indigo-400">{newHires}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {t("teamOverview.openPositions")}
              </p>
            </div>
            <div className="rounded-xl border border-border/60 bg-muted/30 p-3 text-center">
              <p className="text-xl font-bold text-sky-400">{horizon}mo</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {t("teamOverview.timeline")}
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              {t("teamOverview.targetRoles")}
            </p>
            {roles.map((r, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-xs rounded-lg border border-border/50 bg-card px-3 py-2"
              >
                <div className="h-2 w-2 rounded-full gradient-brand bg-indigo-500" />
                {r}
              </div>
            ))}
          </div>

          <p className="text-[11px] text-muted-foreground bg-muted/30 rounded-xl px-3 py-2">
            {t("teamOverview.planNote", { cap: projectedCap, horizon })}
          </p>

          <div className="flex justify-end pt-1 border-t border-border/40">
            <Button
              size="sm"
              className="text-xs gradient-brand text-primary-foreground"
              onClick={onClose}
            >
              {t("teamOverview.exportPlan")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Save Scenario Dialog ──────────────────────────────────────────────────────

function SaveScenarioDialog({
  open,
  onClose,
  onSave,
  t,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  t: (k: string) => string;
}) {
  const [name, setName] = useState("");
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {t("teamOverview.saveScenario")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <input
            className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2"
            placeholder={t("teamOverview.scenarioName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="ghost"
              className="text-xs"
              onClick={onClose}
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              className="text-xs gradient-brand text-primary-foreground"
              disabled={!name.trim()}
              onClick={() => {
                onSave(name.trim());
                setName("");
              }}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Widget ───────────────────────────────────────────────────────────────

interface ForecastWidgetProps {
  members?: TeamMember[];
  isHR?: boolean;
}

export function ForecastWidget({
  members = [],
  isHR = false,
}: ForecastWidgetProps) {
  const { t } = useTranslation();

  const [newHires, setNewHires] = useState(0);
  const [attrition, setAttrition] = useState(0);
  const [horizon, setHorizon] = useState<Horizon>(6);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [showSave, setShowSave] = useState(false);
  const [showPlan, setShowPlan] = useState(false);

  const baseline = members.length;
  const baseCapacity = avgCapacity(members);

  const projected = useMemo(() => {
    const headcount = baseline + newHires - attrition;
    const addedCapacity = newHires * 70;
    const removedCapacity = attrition * baseCapacity;
    const totalCap =
      members.reduce((s, m) => s + m.capacityPercent, 0) +
      addedCapacity -
      removedCapacity;
    const avgCap = headcount > 0 ? Math.round(totalCap / headcount) : 0;
    return { headcount, avgCap };
  }, [newHires, attrition, baseline, baseCapacity, members]);

  const headcountDelta = projected.headcount - baseline;
  const capacityDelta = projected.avgCap - baseCapacity;
  const lvl = riskLevel(projected.avgCap);

  // Hiring recommendation
  const recommendation = useMemo(() => {
    if (projected.avgCap > 90) {
      const rec = Math.max(1, Math.round((projected.avgCap - 85) / 10));
      return t("teamOverview.recHire", { count: rec });
    }
    if (projected.avgCap < 60) return t("teamOverview.recOverstaff");
    return t("teamOverview.recOptimal");
  }, [projected.avgCap, t]);

  // Project risk from mock allocations
  const projectRisks = useMemo(() => {
    return MOCK_PROJECTS.map((proj) => {
      const allocated = members.filter((m) =>
        m.allocations.some((a) => a.projectId === proj.id),
      );
      if (!allocated.length) return null;
      const avgCap = Math.round(
        allocated.reduce((s, m) => s + m.capacityPercent, 0) / allocated.length,
      );
      const totalAlloc = allocated.reduce((s, m) => {
        const a = m.allocations.find((x) => x.projectId === proj.id);
        return s + (a?.percent ?? 0);
      }, 0);
      let risk: "ok" | "risk" | "under" = "ok";
      if (avgCap > 90) risk = "risk";
      else if (totalAlloc < allocated.length * 40) risk = "under";
      return {
        ...proj,
        risk,
        avgCap,
        totalAlloc,
        memberCount: allocated.length,
      };
    }).filter(Boolean) as {
      id: string;
      name: string;
      color: string;
      risk: "ok" | "risk" | "under";
      avgCap: number;
      totalAlloc: number;
      memberCount: number;
    }[];
  }, [members]);

  const riskyProjects = projectRisks.filter((p) => p.risk !== "ok");

  function saveScenario(name: string) {
    setScenarios((prev) => [
      ...prev,
      { id: Date.now().toString(), name, newHires, attrition, horizon },
    ]);
    setShowSave(false);
  }

  function loadScenario(s: Scenario) {
    setNewHires(s.newHires);
    setAttrition(s.attrition);
    setHorizon(s.horizon);
  }

  function deleteScenario(id: string) {
    setScenarios((prev) => prev.filter((s) => s.id !== id));
  }

  const horizonOptions: { value: Horizon; label: string }[] = [
    { value: 3, label: t("teamOverview.next3Months") },
    { value: 6, label: t("teamOverview.next6Months") },
    { value: 12, label: t("teamOverview.next12Months") },
  ];

  return (
    <>
      <Card className="flex flex-col h-full">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-sky-500/10">
                <TrendingUp className="h-4 w-4 text-sky-400" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold">
                  {t("teamOverview.whatIfForecasting")}
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("teamOverview.whatIfSubtitle")}
                </p>
              </div>
            </div>

            {/* Time horizon selector */}
            <div className="flex items-center gap-0.5 rounded-lg border border-border/60 bg-muted/30 p-0.5">
              {horizonOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setHorizon(opt.value)}
                  className={cn(
                    "px-2 py-1 text-[10px] font-medium rounded-md transition-colors",
                    horizon === opt.value
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col gap-4 overflow-y-auto">
          {/* Sliders */}
          <div className="space-y-4">
            <SliderRow
              label={t("teamOverview.addHires")}
              value={newHires}
              min={0}
              max={10}
              onChange={setNewHires}
            />
            <SliderRow
              label={t("teamOverview.attritionRate")}
              value={attrition}
              min={0}
              max={8}
              onChange={setAttrition}
            />
          </div>

          {/* Before / After comparison */}
          <div className="rounded-xl border border-border/60 bg-muted/30 p-3 space-y-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                {t("teamOverview.projectedHeadcount")}
              </p>
              <div className="flex items-center gap-2">
                <MetricChip
                  value={baseline}
                  label={t("teamOverview.currentBaseline")}
                />
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <MetricChip
                  value={projected.headcount}
                  label={t("teamOverview.projected")}
                  delta={headcountDelta}
                  positive={headcountDelta >= 0}
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                {t("teamOverview.projectedCapacity")}
              </p>
              <div className="flex items-center gap-2">
                <MetricChip
                  value={`${baseCapacity}%`}
                  label={t("teamOverview.currentBaseline")}
                />
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <MetricChip
                  value={`${projected.avgCap}%`}
                  label={t("teamOverview.projected")}
                  delta={capacityDelta}
                  positive={capacityDelta <= 0}
                />
              </div>
            </div>
          </div>

          {/* Capacity Risk + Recommendation */}
          <div className="rounded-xl border border-border/60 bg-muted/30 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">
                {t("teamOverview.capacityRisk")}
              </span>
              <RiskBadge cap={projected.avgCap} t={t} />
            </div>
            <p className="text-[11px] text-muted-foreground">
              {recommendation}
            </p>
          </div>

          {/* Project Risk */}
          {riskyProjects.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("teamOverview.projectsAtRisk")}
              </p>
              {riskyProjects.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 text-[11px] rounded-lg bg-muted/30 border border-border/50 px-2.5 py-1.5"
                >
                  <span
                    className="h-2 w-2 rounded-sm shrink-0"
                    style={{ backgroundColor: p.color }}
                  />
                  <span className="flex-1 truncate font-medium">{p.name}</span>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[9px]",
                      p.risk === "risk"
                        ? "border-rose-500/40 text-rose-400 bg-rose-500/10"
                        : "border-sky-500/40 text-sky-400 bg-sky-500/10",
                    )}
                  >
                    {p.risk === "risk"
                      ? t("teamOverview.capacityRiskBadge")
                      : t("teamOverview.underAllocation")}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          {/* Saved scenarios */}
          {scenarios.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("teamOverview.savedScenarios")}
              </p>
              <div className="space-y-1">
                {scenarios.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center gap-2 rounded-lg border border-border/50 bg-card px-2.5 py-1.5"
                  >
                    <Bookmark className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="text-xs flex-1 truncate">{s.name}</span>
                    <span className="text-[10px] text-muted-foreground">
                      +{s.newHires}h −{s.attrition}a · {s.horizon}mo
                    </span>
                    <button
                      onClick={() => loadScenario(s)}
                      className="text-xs text-indigo-400 hover:text-indigo-300 shrink-0"
                    >
                      {t("teamOverview.loadScenario")}
                    </button>
                    <button
                      onClick={() => deleteScenario(s.id)}
                      className="text-muted-foreground hover:text-destructive shrink-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mt-auto pt-2 border-t border-border/40">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-xs gap-1.5"
              onClick={() => setShowSave(true)}
            >
              <BookmarkPlus className="h-3 w-3" />
              {t("teamOverview.saveScenario")}
            </Button>
            {isHR && (
              <Button
                size="sm"
                className="flex-1 text-xs gap-1.5 gradient-brand text-primary-foreground"
                disabled={newHires === 0}
                onClick={() => setShowPlan(true)}
              >
                <ClipboardList className="h-3 w-3" />
                {t("teamOverview.createHiringPlan")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <SaveScenarioDialog
        open={showSave}
        onClose={() => setShowSave(false)}
        onSave={saveScenario}
        t={t}
      />

      <HiringPlanDialog
        open={showPlan}
        onClose={() => setShowPlan(false)}
        newHires={newHires}
        horizon={horizon}
        projectedCap={projected.avgCap}
        t={t}
      />
    </>
  );
}
